import { useCallback, useRef } from "react";

const MIN_W = 200;
const MIN_H = 120;
const COLLAPSED_H = 46; // title bar only

/**
 * HudPanel — a single free-floating, draggable / resizable card.
 *
 * Props:
 *   id, title
 *   rect       { x, y, w, h }
 *   collapsed  boolean
 *   locked     boolean            — disables drag + resize
 *   z          number             — stacking order
 *   onChange(patch)               — { x, y, w, h, collapsed }
 *   onClose()
 *   onFocus()                     — bring-to-front (pointer-down)
 *   children
 */
export default function HudPanel({
  id,
  title,
  rect,
  collapsed = false,
  locked = false,
  z = 1,
  onChange,
  onClose,
  onFocus,
  children,
}) {
  const dragRef = useRef(null);

  const clamp = useCallback((x, y, w, h) => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // Keep at least a sliver on-screen so a panel can't be lost.
    const maxX = Math.max(0, vw - 40);
    const maxY = Math.max(0, vh - 40);
    return {
      x: Math.min(Math.max(x, 8 - (w - 40)), maxX),
      y: Math.min(Math.max(y, 8), maxY),
    };
  }, []);

  const startDrag = useCallback(
    (e) => {
      if (locked) return;
      // Don't start a drag from the header buttons.
      if (e.target.closest("button")) return;
      if (e.button != null && e.button !== 0) return;
      e.preventDefault();
      const target = e.currentTarget;
      target.setPointerCapture?.(e.pointerId);

      const start = {
        px: e.clientX,
        py: e.clientY,
        x: rect.x,
        y: rect.y,
        w: rect.w,
        h: rect.h,
      };
      dragRef.current = start;

      const move = (ev) => {
        const nx = start.x + (ev.clientX - start.px);
        const ny = start.y + (ev.clientY - start.py);
        const c = clamp(nx, ny, start.w, start.h);
        onChange({ x: c.x, y: c.y });
      };
      const up = (ev) => {
        target.releasePointerCapture?.(ev.pointerId);
        target.removeEventListener("pointermove", move);
        target.removeEventListener("pointerup", up);
        target.removeEventListener("pointercancel", up);
        dragRef.current = null;
      };
      target.addEventListener("pointermove", move);
      target.addEventListener("pointerup", up);
      target.addEventListener("pointercancel", up);
    },
    [locked, rect.x, rect.y, rect.w, rect.h, onChange, clamp]
  );

  const startResize = useCallback(
    (e) => {
      if (locked || collapsed) return;
      if (e.button != null && e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      const target = e.currentTarget;
      target.setPointerCapture?.(e.pointerId);

      const start = {
        px: e.clientX,
        py: e.clientY,
        w: rect.w,
        h: rect.h,
      };

      const move = (ev) => {
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        let nw = start.w + (ev.clientX - start.px);
        let nh = start.h + (ev.clientY - start.py);
        nw = Math.max(MIN_W, Math.min(nw, vw - rect.x - 8));
        nh = Math.max(MIN_H, Math.min(nh, vh - rect.y - 8));
        onChange({ w: nw, h: nh });
      };
      const up = (ev) => {
        target.releasePointerCapture?.(ev.pointerId);
        target.removeEventListener("pointermove", move);
        target.removeEventListener("pointerup", up);
        target.removeEventListener("pointercancel", up);
      };
      target.addEventListener("pointermove", move);
      target.addEventListener("pointerup", up);
      target.addEventListener("pointercancel", up);
    },
    [locked, collapsed, rect.w, rect.h, rect.x, rect.y, onChange]
  );

  const style = {
    left: `${rect.x}px`,
    top: `${rect.y}px`,
    width: `${rect.w}px`,
    height: collapsed ? undefined : `${rect.h}px`,
    zIndex: z,
  };

  const titleId = `hud-title-${id}`;

  return (
    <section
      className={`hud-panel${collapsed ? " is-collapsed" : ""}${
        locked ? " is-locked" : ""
      }`}
      style={style}
      role="dialog"
      aria-label={title}
      aria-labelledby={titleId}
      onPointerDownCapture={onFocus}
    >
      <header
        className="hud-panel-bar"
        onPointerDown={startDrag}
        style={{ cursor: locked ? "default" : "grab" }}
      >
        <span className="hud-panel-title" id={titleId}>
          {title}
        </span>
        <div className="hud-panel-actions">
          <button
            type="button"
            className="hud-icon-btn"
            aria-label={collapsed ? `Expand ${title}` : `Collapse ${title}`}
            aria-expanded={!collapsed}
            onClick={() => onChange({ collapsed: !collapsed })}
          >
            {collapsed ? "▸" : "▾"}
          </button>
          <button
            type="button"
            className="hud-icon-btn"
            aria-label={`Close ${title}`}
            onClick={onClose}
          >
            ✕
          </button>
        </div>
      </header>

      {!collapsed && (
        <div className="hud-panel-body" style={{ height: rect.h - COLLAPSED_H }}>
          {children}
        </div>
      )}

      {!collapsed && !locked && (
        <div
          className="hud-resize"
          role="separator"
          aria-label={`Resize ${title}`}
          onPointerDown={startResize}
        />
      )}
    </section>
  );
}
